import { Flex, Menu } from "antd"
import React, { memo, useCallback, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"

import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { invoicesHiddenForRole } from "../../utils/invoices-visibility.ts"
import { canAccessArchivePage } from "../Archive/archive-permissions.ts"
import { type PermissionPages } from "../SettingsPermissions/types/permissions.types.ts"
import MenuComponent from "../User/NavUserMenu.tsx"
import { SidebarContextMenu } from "./SidebarContextMenu.tsx"
import {
  ARCHIVE_NAV_KEY,
  INVOICES_NAV_KEY,
  REPORTS_NAV_KEY,
  type SidebarItem,
  sidebarConfig
} from "./sidebar-config.tsx"
import "./styles.less"

interface SidebarProps {
  onItemSelect: (item: Pick<SidebarItem, "key">) => void
  showDefaultOpenKeys?: boolean
}

// A nav key is its route, and the permission page is that route with the
// slashes swapped.
const navKeyToPage = (key: string): PermissionPages =>
  key?.replace(/\//g, "_") as PermissionPages

export const SidebarBody = memo(
  ({ onItemSelect, showDefaultOpenKeys }: SidebarProps) => {
    const location = useLocation()
    const permissions = useAppSelector((state) => state.auth.permissions)
    const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)
    const invoicesHidden = invoicesHiddenForRole(roleId)
    const pages = [...(permissions?.pages || []), "document_templates"]

    const getAllKeysWithParent = (): Array<{
      key: string
      parentKey?: string
    }> => {
      const items = [...sidebarConfig.main, ...sidebarConfig.bottom]

      return items.flatMap((item) =>
        "children" in item && item.children
          ? item.children.map((c) => ({
              key: String(c?.key),
              parentKey: String(item.key)
            }))
          : [{ key: String(item.key) }]
      )
    }

    const currentKeys = useMemo(() => {
      const path = location.pathname.slice(1)
      const allKeys = getAllKeysWithParent()

      const matched = allKeys
        .filter(({ key }) => path === key || path.startsWith(`${key}/`))
        .sort((a, b) => b.key.length - a.key.length)[0]

      if (!matched) return []

      return matched.parentKey
        ? [matched.parentKey, matched.key]
        : [matched.key]
    }, [location.pathname])

    const mainMenuItems: SidebarItem[] = useMemo(
      () =>
        sidebarConfig.main?.reduce((list, item) => {
          if ("children" in item && item?.children?.length) {
            const filteredItems = item.children.filter((child) =>
              pages?.includes(navKeyToPage(child?.key as string))
            )

            if (filteredItems?.length) {
              list.push({ ...item, children: filteredItems })
            }
          } else {
            // Reports is open to every authenticated user.
            if (item?.key === REPORTS_NAV_KEY) {
              list.push(item)
            } else if (item?.key === INVOICES_NAV_KEY) {
              if (pages?.includes(INVOICES_NAV_KEY) && !invoicesHidden) {
                list.push(item)
              }
            } else if (item?.key === ARCHIVE_NAV_KEY) {
              // Archive merges two former entry points — the "Inactive" leaf
              // under Leads and the Archive tab on Projects — so either
              // audience that could reach one of those still reaches this.
              if (canAccessArchivePage(permissions)) {
                list.push(item)
              }
            } else if (pages?.includes(navKeyToPage(item?.key as string))) {
              list.push(item)
            }
          }

          return list
        }, [] as SidebarItem[]),
      [pages, permissions, invoicesHidden]
    )

    const defaultOpenKeys = showDefaultOpenKeys ? currentKeys : []

    // A right-clickable menu row maps to the route its left click would open:
    // a leaf to its own key, a parent (Approval) to its first permitted child.
    const routeByKey = useMemo(() => {
      const map = new Map<string, string>()
      const collect = (items: SidebarItem[]) => {
        items.forEach((item) => {
          const key = String(item.key)
          if ("children" in item && item.children?.length) {
            item.children.forEach((child) => {
              const childKey = (child as { key?: React.Key })?.key
              if (childKey != null) map.set(String(childKey), `/${childKey}`)
            })
            const firstChildKey = (
              item.children[0] as { key?: React.Key }
            )?.key
            if (firstChildKey != null) map.set(key, `/${firstChildKey}`)
          } else {
            map.set(key, `/${key}`)
          }
        })
      }
      collect(mainMenuItems)
      collect(sidebarConfig.bottom)
      return map
    }, [mainMenuItems])

    const [contextMenu, setContextMenu] = useState<{
      x: number
      y: number
      route: string
    } | null>(null)

    const handleContextMenu = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        // Leaf rows carry data-menu-id on the <li>; a parent (Approval) carries
        // it on its submenu-title element instead.
        const row = (event.target as HTMLElement).closest<HTMLElement>(
          "[data-menu-id]"
        )
        if (!row) return

        const menuId = row.getAttribute("data-menu-id") ?? ""
        const matchedKey = [...routeByKey.keys()].find(
          (key) => menuId === key || menuId.endsWith(`-${key}`)
        )
        if (!matchedKey) return

        event.preventDefault()
        setContextMenu({
          x: event.clientX,
          y: event.clientY,
          route: routeByKey.get(matchedKey) as string
        })
      },
      [routeByKey]
    )

    const openInNewTab = useCallback(() => {
      if (contextMenu) {
        window.open(contextMenu.route, "_blank", "noopener,noreferrer")
      }
      setContextMenu(null)
    }, [contextMenu])

    return (
      <Flex vertical className={"h-full"} onContextMenu={handleContextMenu}>
        <SidebarContextMenu
          open={contextMenu != null}
          x={contextMenu?.x ?? 0}
          y={contextMenu?.y ?? 0}
          onOpenChange={(next) => {
            if (!next) setContextMenu(null)
          }}
          onOpenInNewTab={openInNewTab}
        />
        <Flex
          vertical
          justify={"space-between"}
          flex={1}
          className={"overflow-auto"}
        >
          <Menu
            mode="inline"
            selectedKeys={currentKeys}
            defaultOpenKeys={defaultOpenKeys}
            items={mainMenuItems}
            onClick={onItemSelect}
            className="custom-sidebar-menu border-0 flex-1"
            style={{ backgroundColor: "transparent" }}
          />

          <Menu
            mode="inline"
            selectedKeys={currentKeys}
            defaultOpenKeys={defaultOpenKeys}
            items={sidebarConfig.bottom}
            onClick={onItemSelect}
            className="custom-sidebar-menu border-r-0 border-b border-border"
            style={{ backgroundColor: "transparent" }}
          />
        </Flex>
        <MenuComponent />
      </Flex>
    )
  }
)

SidebarBody.displayName = "SidebarBody"
